import { ApiError, apiRequest } from '@scc/lib'
import { useSessionStore } from '@/lib/session.store'
import type { FindDetail, ServerFind } from './finds.store'
import type { SlotId } from './ids'

interface PostFindResponse {
  slotId: SlotId
  foundAt: string
  alreadyFound: boolean
}

interface FindsResponse {
  finds: ServerFind[]
  resetAt?: string | null
}

export interface ServerProgress {
  readonly finds: readonly ServerFind[]
  /** When an admin last reset this person; null if never. */
  readonly resetAt: string | null
}

interface QueuedFind {
  readonly slotId: SlotId
  readonly detail?: FindDetail
}

const FINDS_PATH = '/me/halloween/finds'

const queueKey = (): string =>
  `scc:halloween:v1:queue:${useSessionStore.getState().user.email.toLowerCase()}`

function readQueue(): readonly QueuedFind[] {
  try {
    const raw = localStorage.getItem(queueKey())
    return raw ? (JSON.parse(raw) as QueuedFind[]) : []
  } catch {
    return []
  }
}

function writeQueue(queue: readonly QueuedFind[]): void {
  try {
    localStorage.setItem(queueKey(), JSON.stringify(queue))
  } catch {
    // ignore: queue is best effort
  }
}

function enqueue(item: QueuedFind): void {
  const queue = readQueue()
  if (queue.some((q) => q.slotId === item.slotId)) return
  writeQueue([...queue, item])
}

function send(item: QueuedFind): Promise<PostFindResponse> {
  return apiRequest<PostFindResponse>(FINDS_PATH, {
    method: 'POST',
    body: { slotId: item.slotId, ...(item.detail ? { detail: item.detail } : {}) },
  })
}

/** A 4xx other than 408 and 429 will never succeed on retry, so the item is dropped. */
function isPermanentFailure(error: Error): boolean {
  if (!(error instanceof ApiError)) return false
  const { status } = error
  return status >= 400 && status < 500 && status !== 408 && status !== 429
}

/** Records a find on the server; on any failure the find is queued for the next flush. */
export async function postFind(slotId: SlotId, detail?: FindDetail): Promise<void> {
  const item: QueuedFind = { slotId, ...(detail ? { detail } : {}) }
  try {
    await send(item)
  } catch (error) {
    if (error instanceof Error && isPermanentFailure(error)) return
    enqueue(item)
  }
}

/** Retries queued finds once each; successes and permanent 4xx failures leave the queue, other failures stay. */
export async function flushQueue(): Promise<void> {
  const queue = readQueue()
  if (queue.length === 0) return
  const results = await Promise.all(
    queue.map((item) =>
      send(item).then(
        () => true,
        (error: Error) => isPermanentFailure(error),
      ),
    ),
  )
  const remaining = queue.filter((_, i) => !results[i])
  const current = readQueue().filter((q) => !queue.some((s) => s.slotId === q.slotId))
  writeQueue([...remaining, ...current])
}

/** Drops finds that were waiting to be sent, e.g. because an admin reset the person's progress. */
export function clearQueue(): void {
  try {
    localStorage.removeItem(queueKey())
  } catch {
    // ignore: queue is best effort
  }
}

/** On any failure this is empty with no reset, so a failed fetch can never wipe local progress. */
export async function fetchMyProgress(): Promise<ServerProgress> {
  try {
    const res = await apiRequest<FindsResponse>(FINDS_PATH)
    return { finds: res.finds, resetAt: res.resetAt ?? null }
  } catch {
    return { finds: [], resetAt: null }
  }
}
